import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";

const LOGIN_IDS = [10253, 10406, 10130, 10354, 10321];
const F11_BRANCH_ID = "15";
const G13_BRANCH_ID = "1";

const normalizeRows = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const isExcludedLogin = (id) => LOGIN_IDS.includes(Number(id));

const isF11Branch = (branchId) => String(branchId) === F11_BRANCH_ID;
const isG13Branch = (branchId) => String(branchId) === G13_BRANCH_ID;

const getEmployeeName = (employee = {}, fallback = "N/A") => {
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return fullName || fallback;
};

const formatDate = (value, fallback = "N/A") =>
  value && moment(value).isValid() ? moment(value).format("MMM DD, YYYY") : fallback;

const formatTime = (value, format = "HH:mm:ss") => {
  if (!value) return "N/A";
  const parsed = moment(value, format, true);
  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};

const getBranchLabel = (branchId, branchName) => {
  if (branchName) return branchName;
  if (isF11Branch(branchId)) return "F-11";
  if (isG13Branch(branchId)) return "G-13";
  return "N/A";
};

const createDaySummary = (label, date) => ({
  label,
  date,
  present: 0,
  late: 0,
  absent: 0,
  onLeave: 0,
  f11: { present: 0, late: 0, absent: 0, onLeave: 0 },
  g13: { present: 0, late: 0, absent: 0, onLeave: 0 },
});

const buildDutyHoursMap = (rows) => {
  const map = {};

  rows.forEach((staff) => {
    if (!Array.isArray(staff.duty_hours)) return;

    map[String(staff.id)] = {};
    staff.duty_hours.forEach((slot) => {
      if (String(slot.status) === "1") {
        map[String(staff.id)][slot.day] = {
          start_time: slot.start_time,
          end_time: slot.end_time,
        };
      }
    });
  });

  return map;
};

const calculateLateDetails = (attendance, dutyHoursMap) => {
  const staffDutyHours = dutyHoursMap[String(attendance.attendee_id)] || {};
  const dayName = moment(attendance.date).format("dddd");
  const dayDuty = staffDutyHours[dayName];

  if (!dayDuty?.start_time || !attendance.checkin_time_24h) {
    return {
      isLate: false,
      lateMinutes: 0,
      lateText: "On Time",
      dutyStartTime: "N/A",
      dutyEndTime: "N/A",
    };
  }

  const dutyStart = moment(dayDuty.start_time, "HH:mm", true);
  const dutyEnd = dayDuty.end_time
    ? moment(dayDuty.end_time, "HH:mm", true)
    : null;
  const checkIn = moment(attendance.checkin_time_24h, "HH:mm:ss", true);

  if (!dutyStart.isValid() || !checkIn.isValid()) {
    return {
      isLate: false,
      lateMinutes: 0,
      lateText: "On Time",
      dutyStartTime: formatTime(dayDuty.start_time, "HH:mm"),
      dutyEndTime: formatTime(dayDuty.end_time, "HH:mm"),
    };
  }

  const lateMinutes = checkIn.isAfter(dutyStart)
    ? checkIn.diff(dutyStart, "minutes")
    : 0;

  return {
    isLate: lateMinutes > 0,
    lateMinutes,
    lateText:
      lateMinutes > 0
        ? `${Math.floor(lateMinutes / 60) > 0 ? `${Math.floor(lateMinutes / 60)}h ` : ""}${
            lateMinutes % 60
          }m`.trim()
        : "On Time",
    dutyStartTime: dutyStart.format("hh:mm A"),
    dutyEndTime: dutyEnd?.isValid() ? dutyEnd.format("hh:mm A") : "N/A",
  };
};

const sortRowsByDateAndName = (rows) =>
  [...rows].sort((a, b) => {
    const dateCompare = moment(a.date).diff(moment(b.date));
    if (dateCompare !== 0) return dateCompare;
    return (a.name || "").localeCompare(b.name || "");
  });

const SummaryCard = ({ title, value, tone, children }) => (
  <div className="col-12 col-md-6 col-xl-4 mb-3">
    <div className={`card radius-10 custom-shadow h-100 border-${tone}`}>
      <div className="card-body">
        <p className="text-muted mb-2">{title}</p>
        <h2 className={`mb-3 fw-bold text-${tone}`}>{value}</h2>
        {children}
      </div>
    </div>
  </div>
);

const AttendanceDayCard = ({ summary, tone }) => (
  <div className="col-12 col-lg-4 mb-3">
    <div className={`card radius-10 custom-shadow h-100 border-${tone}`}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className={`mb-1 text-${tone}`}>{summary.label}</h5>
            <p className="text-muted mb-0">{formatDate(summary.date)}</p>
          </div>
          <span className={`badge bg-${tone}`}>Summary</span>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6">
            <div className="rounded border bg-light p-2 h-100">
              <div className="small text-muted">Present</div>
              <div className="fw-bold">{summary.present}</div>
            </div>
          </div>
          <div className="col-6">
            <div className="rounded border bg-light p-2 h-100">
              <div className="small text-muted">On Leave</div>
              <div className="fw-bold">{summary.onLeave}</div>
            </div>
          </div>
          <div className="col-6">
            <div className="rounded border bg-light p-2 h-100">
              <div className="small text-muted">Late</div>
              <div className="fw-bold text-warning">{summary.late}</div>
            </div>
          </div>
          <div className="col-6">
            <div className="rounded border bg-light p-2 h-100">
              <div className="small text-muted">Absent</div>
              <div className="fw-bold text-danger">{summary.absent}</div>
            </div>
          </div>
        </div>

        <div className="small text-muted">
          <div>
            F-11: P {summary.f11.present} | L {summary.f11.late} | A {summary.f11.absent} | Lv{" "}
            {summary.f11.onLeave}
          </div>
          <div>
            G-13: P {summary.g13.present} | L {summary.g13.late} | A {summary.g13.absent} | Lv{" "}
            {summary.g13.onLeave}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MetricSummaryCard = ({ title, tone, summaries, keyName }) => (
  <div className="col-12 col-lg-6 mb-3">
    <div className={`card radius-10 custom-shadow h-100 border-${tone}`}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className={`mb-1 text-${tone}`}>{title}</h5>
            <p className="text-muted mb-0">Quick day-wise summary</p>
          </div>
          <span className={`badge bg-${tone}`}>
            {summaries.reduce((sum, item) => sum + item[keyName], 0)}
          </span>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {summaries.map((item) => (
            <span key={`${title}-${item.date}`} className="badge text-bg-light border">
              {item.label}: {item[keyName]}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const DetailAccordionItem = ({ id, title, tone, count, children }) => (
  <div className="accordion-item">
    <h2 className="accordion-header" id={`${id}-heading`}>
      <button
        className="accordion-button collapsed d-flex justify-content-between align-items-center gap-3"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target={`#${id}-collapse`}
        aria-expanded="false"
        aria-controls={`${id}-collapse`}
      >
        <span className={`fw-semibold text-${tone}`}>{title}</span>
        <span className={`badge bg-${tone}`}>{count}</span>
      </button>
    </h2>
    <div
      id={`${id}-collapse`}
      className="accordion-collapse collapse"
      aria-labelledby={`${id}-heading`}
    >
      <div className="accordion-body">{children}</div>
    </div>
  </div>
);

const DataTable = ({ rows, columns, emptyMessage }) => {
  if (!rows.length) {
    return <p className="text-muted text-center py-3 mb-0">{emptyMessage}</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover table-striped align-middle mb-0">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.label}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || row.uid || `${row.name || "row"}-${index}`}>
              {columns.map((column) => (
                <td key={`${column.label}-${index}`}>
                  {column.render ? column.render(row, index) : row[column.key] || "N/A"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const HRDashboard = () => {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const yesterdayKey = moment().subtract(1, "day").format("YYYY-MM-DD");
  const todayKey = moment().format("YYYY-MM-DD");
  const tomorrowKey = moment().add(1, "day").format("YYYY-MM-DD");

  const [selectedBranch, setSelectedBranch] = useState("all");
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  const [staffSummary, setStaffSummary] = useState({
    total: 0,
    f11: 0,
    g13: 0,
  });
  const [departmentSummary, setDepartmentSummary] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState({
    yesterday: createDaySummary("Yesterday", yesterdayKey),
    today: createDaySummary("Today", todayKey),
    tomorrow: createDaySummary("Tomorrow", tomorrowKey),
  });
  const [staffDetails, setStaffDetails] = useState([]);
  const [presentEmployees, setPresentEmployees] = useState([]);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [onLeaveEmployees, setOnLeaveEmployees] = useState([]);
  const [birthdaysThisMonth, setBirthdaysThisMonth] = useState([]);
  const [anniversariesThisMonth, setAnniversariesThisMonth] = useState([]);

  const fetchBranches = async () => {
    try {
      const response = await UserServices.branchName();
      if (response.status === 200) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const activeBranch = selectedBranch === "all" ? "" : selectedBranch || branch_id || "";

      const [staffResult, dutyHoursResult, attendanceResult, leaveResult] =
        await Promise.allSettled([
          UserServices.getactiveUsers(activeBranch, "1", "1000", "1", "", ""),
          UserServices.getActiveEmployeHours(activeBranch, "1000", "1"),
          UserServices.getEmployeeAttendence(
            activeBranch,
            {
              sdate: yesterdayKey,
              edate: tomorrowKey,
              start_time: null,
              end_time: null,
              gender: null,
            },
            null,
            "1000",
            "1"
          ),
          UserServices.getLeaveApplications(activeBranch, "1000", "1"),
        ]);

      if (staffResult.status !== "fulfilled" || staffResult.value.status !== 200) {
        throw new Error("Failed to load staff records");
      }

      const allStaff = normalizeRows(staffResult.value);
      const dutyHoursRows =
        dutyHoursResult.status === "fulfilled" && dutyHoursResult.value.status === 200
          ? normalizeRows(dutyHoursResult.value)
          : [];
      const attendanceRows =
        attendanceResult.status === "fulfilled" && attendanceResult.value.status === 200
          ? normalizeRows(attendanceResult.value)
          : [];
      const leaveRows =
        leaveResult.status === "fulfilled" && leaveResult.value.status === 200
          ? normalizeRows(leaveResult.value)
          : [];

      const scopedStaff = allStaff.filter((staff) => !isExcludedLogin(staff.id));
      const f11Staff = scopedStaff.filter((staff) => isF11Branch(staff.branch_id));
      const g13Staff = scopedStaff.filter((staff) => isG13Branch(staff.branch_id));
      const dutyHoursMap = buildDutyHoursMap(dutyHoursRows);

      const staffLookup = {};
      scopedStaff.forEach((staff) => {
        staffLookup[String(staff.id)] = staff;
      });

      setStaffSummary({
        total: scopedStaff.length,
        f11: f11Staff.length,
        g13: g13Staff.length,
      });

      const departmentMap = {};
      scopedStaff.forEach((staff) => {
        const departmentName = staff.department_name || staff.department || "Unassigned";
        const designationName = staff.designation_name || staff.designation || "Unassigned";

        if (!departmentMap[departmentName]) {
          departmentMap[departmentName] = {
            id: departmentName,
            department: departmentName,
            total: 0,
            f11: 0,
            g13: 0,
            designations: {},
          };
        }

        departmentMap[departmentName].total += 1;
        departmentMap[departmentName].designations[designationName] =
          (departmentMap[departmentName].designations[designationName] || 0) + 1;

        if (isF11Branch(staff.branch_id)) departmentMap[departmentName].f11 += 1;
        if (isG13Branch(staff.branch_id)) departmentMap[departmentName].g13 += 1;
      });

      const departmentRows = Object.values(departmentMap)
        .map((department) => ({
          ...department,
          designationSummary: Object.entries(department.designations)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name, count]) => `${name} (${count})`)
            .join(", "),
          topDesignations: Object.entries(department.designations)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .slice(0, 2),
        }))
        .sort((a, b) => b.total - a.total || a.department.localeCompare(b.department));

      setDepartmentSummary(departmentRows);

      const staffDirectoryRows = [...scopedStaff]
        .map((staff) => ({
          id: staff.id,
          uid: staff.uid || "N/A",
          name: getEmployeeName(staff),
          branch: getBranchLabel(staff.branch_id, staff.branch_name),
          department: staff.department_name || staff.department || "N/A",
          designation: staff.designation_name || staff.designation || "N/A",
          joining: staff.joining,
          phone: staff.phone || "N/A",
        }))
        .sort(
          (a, b) =>
            a.department.localeCompare(b.department) || a.name.localeCompare(b.name)
        );

      setStaffDetails(staffDirectoryRows);

      const daySummaryMap = {
        [yesterdayKey]: createDaySummary("Yesterday", yesterdayKey),
        [todayKey]: createDaySummary("Today", todayKey),
        [tomorrowKey]: createDaySummary("Tomorrow", tomorrowKey),
      };

      const presentRows = [];
      const lateRows = [];
      const absentRows = [];

      attendanceRows.forEach((attendance) => {
        const staffId = String(attendance.attendee_id);
        const attendanceDate = attendance.date;

        if (!daySummaryMap[attendanceDate] || isExcludedLogin(staffId)) return;

        const staff = staffLookup[staffId] || {};
        const branchId = staff.branch_id || attendance.branch_id;
        const branchKey = isF11Branch(branchId) ? "f11" : isG13Branch(branchId) ? "g13" : null;
        const lateDetails = calculateLateDetails(attendance, dutyHoursMap);
        const isPresent = attendance.attendance_status === "Present";
        const isAbsent = attendance.attendance_status === "Absent";

        const baseRow = {
          id: `${attendance.id || staffId}-${attendanceDate}`,
          name: getEmployeeName(attendance.attendee || staff, attendance.user_name || "N/A"),
          date: attendanceDate,
          branch_id: branchId,
          branch: getBranchLabel(branchId, staff.branch_name),
          designation:
            attendance.designation ||
            staff.designation_name ||
            staff.designation ||
            "N/A",
          department: staff.department_name || staff.department || "N/A",
          duty_start_time: lateDetails.dutyStartTime,
          duty_end_time: lateDetails.dutyEndTime,
          checkin_time: formatTime(
            attendance.checkin_time_24h || attendance.checkin_time_12h,
            attendance.checkin_time_24h ? "HH:mm:ss" : "hh:mm A"
          ),
          checkout_time: formatTime(
            attendance.checkout_time_24h || attendance.checkout_time_12h,
            attendance.checkout_time_24h ? "HH:mm:ss" : "hh:mm A"
          ),
          working_hours: attendance.working_hours || "N/A",
        };

        if (isPresent) {
          daySummaryMap[attendanceDate].present += 1;
          if (branchKey) daySummaryMap[attendanceDate][branchKey].present += 1;

          presentRows.push({
            ...baseRow,
            status: lateDetails.isLate ? "Late" : "Present",
          });

          if (lateDetails.isLate) {
            daySummaryMap[attendanceDate].late += 1;
            if (branchKey) daySummaryMap[attendanceDate][branchKey].late += 1;

            lateRows.push({
              ...baseRow,
              late_minutes: lateDetails.lateMinutes,
              late_time: lateDetails.lateText,
            });
          }
        }

        if (isAbsent) {
          daySummaryMap[attendanceDate].absent += 1;
          if (branchKey) daySummaryMap[attendanceDate][branchKey].absent += 1;

          absentRows.push({
            ...baseRow,
            reason: attendance.reason || "N/A",
            remarks: attendance.remarks || "N/A",
          });
        }
      });

      const approvedLeaves = leaveRows.filter(
        (leave) => leave.application_status === "Approved"
      );

      const onLeaveRows = [];
      approvedLeaves.forEach((leave, index) => {
        const userId = String(
          leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id || ""
        );

        if (!userId || isExcludedLogin(userId) || !staffLookup[userId]) return;

        const staff = staffLookup[userId];
        const branchId = staff.branch_id;
        const branchKey = isF11Branch(branchId) ? "f11" : isG13Branch(branchId) ? "g13" : null;

        [yesterdayKey, todayKey, tomorrowKey].forEach((dateKey) => {
          if (
            moment(dateKey).isBetween(
              leave.leave_from_date,
              leave.leave_to_date,
              undefined,
              "[]"
            )
          ) {
            daySummaryMap[dateKey].onLeave += 1;
            if (branchKey) daySummaryMap[dateKey][branchKey].onLeave += 1;

            onLeaveRows.push({
              id: `${leave.id || index}-${userId}-${dateKey}`,
              name: getEmployeeName(staff, getEmployeeName(leave.user_info)),
              date: dateKey,
              leave_from_date: leave.leave_from_date,
              leave_to_date: leave.leave_to_date,
              leave_type: leave.leave_type || "N/A",
              reason: leave.reason || leave.remarks || "N/A",
              branch: getBranchLabel(branchId, staff.branch_name),
              designation: staff.designation_name || staff.designation || "N/A",
              department: staff.department_name || staff.department || "N/A",
            });
          }
        });
      });

      setAttendanceSummary({
        yesterday: daySummaryMap[yesterdayKey],
        today: daySummaryMap[todayKey],
        tomorrow: daySummaryMap[tomorrowKey],
      });
      setPresentEmployees(sortRowsByDateAndName(presentRows));
      setLateEmployees(
        [...lateRows].sort((a, b) => {
          const dateCompare = moment(a.date).diff(moment(b.date));
          if (dateCompare !== 0) return dateCompare;
          return b.late_minutes - a.late_minutes;
        })
      );
      setAbsentEmployees(sortRowsByDateAndName(absentRows));
      setOnLeaveEmployees(sortRowsByDateAndName(onLeaveRows));

      const currentMonth = moment().month();
      const today = moment();

      const birthdayRows = scopedStaff
        .filter((staff) => {
          if (!staff.dob) return false;
          const dob = moment(staff.dob);
          return dob.isValid() && dob.month() === currentMonth;
        })
        .map((staff) => {
          const dob = moment(staff.dob);
          return {
            ...staff,
            birthday_date: dob.format("MMM DD"),
            age: today.diff(dob, "years"),
          };
        })
        .sort((a, b) => moment(a.dob).date() - moment(b.dob).date());

      const anniversaryRows = scopedStaff
        .filter((staff) => {
          if (!staff.joining) return false;
          const joiningDate = moment(staff.joining);
          if (!joiningDate.isValid()) return false;

          const anniversaryThisYear = joiningDate.clone().year(today.year());
          return (
            anniversaryThisYear.month() === currentMonth &&
            anniversaryThisYear.isSameOrAfter(today, "day")
          );
        })
        .map((staff) => {
          const joiningDate = moment(staff.joining);
          const anniversaryThisYear = joiningDate.clone().year(today.year());
          return {
            ...staff,
            anniversary_date: anniversaryThisYear.format("MMM DD, YYYY"),
            years_of_service: today.diff(joiningDate, "years"),
          };
        })
        .sort((a, b) => moment(a.anniversary_date).diff(moment(b.anniversary_date)));

      setBirthdaysThisMonth(birthdayRows);
      setAnniversariesThisMonth(anniversaryRows);
    } catch (error) {
      console.error("Error fetching HR dashboard data:", error);
      setStaffSummary({ total: 0, f11: 0, g13: 0 });
      setDepartmentSummary([]);
      setAttendanceSummary({
        yesterday: createDaySummary("Yesterday", yesterdayKey),
        today: createDaySummary("Today", todayKey),
        tomorrow: createDaySummary("Tomorrow", tomorrowKey),
      });
      setStaffDetails([]);
      setPresentEmployees([]);
      setLateEmployees([]);
      setAbsentEmployees([]);
      setOnLeaveEmployees([]);
      setBirthdaysThisMonth([]);
      setAnniversariesThisMonth([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedBranch, branch_id]);

  const selectedBranchLabel =
    selectedBranch === "all"
      ? "All Branches"
      : branches.find((branch) => String(branch.id) === String(selectedBranch))?.name || "Branch";

  const daySummaries = [
    attendanceSummary.yesterday,
    attendanceSummary.today,
    attendanceSummary.tomorrow,
  ];

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />

          <div className="row mb-3">
            <div className="col-12">
              <h4 className="mb-0">HR Dashboard</h4>
              <p className="text-muted mb-0">
                Summary-first HR view with details moved to collapsed sections.
              </p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <div className="card radius-10 custom-shadow">
                <div className="card-body">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label">Branch</label>
                      <select
                        className="form-select"
                        value={selectedBranch}
                        onChange={(event) => setSelectedBranch(event.target.value)}
                      >
                        <option value="all">All Branches</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <div className="small text-muted mb-2">Attendance Window</div>
                      <div className="fw-semibold">
                        {formatDate(yesterdayKey)} to {formatDate(tomorrowKey)}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <button
                        className="btn btn-primary w-100"
                        onClick={fetchData}
                        disabled={loading}
                      >
                        {loading ? "Refreshing..." : "Refresh Dashboard"}
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 small text-muted">
                    Current scope: {selectedBranchLabel}. Summary cards stay visible, while details
                    remain collapsed at the bottom unless opened.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-12">
              <h5 className="mb-3">Staff Overview</h5>
            </div>
          </div>

          <div className="row mb-4">
            {loading ? (
              <>
                <div className="col-12 col-md-6 col-xl-4 mb-3">
                  <Skeleton height={150} />
                </div>
                <div className="col-12 col-md-6 col-xl-4 mb-3">
                  <Skeleton height={150} />
                </div>
                <div className="col-12 col-md-6 col-xl-4 mb-3">
                  <Skeleton height={150} />
                </div>
              </>
            ) : (
              <>
                <SummaryCard title="Total Staff" value={staffSummary.total} tone="primary">
                  <div className="small text-muted">
                    Active staff in scope, excluding HR login accounts.
                  </div>
                </SummaryCard>
                <SummaryCard title="F-11 Staff" value={staffSummary.f11} tone="info">
                  <span className="badge text-bg-light border">Branch Total</span>
                </SummaryCard>
                <SummaryCard title="G-13 Staff" value={staffSummary.g13} tone="success">
                  <span className="badge text-bg-light border">Branch Total</span>
                </SummaryCard>
              </>
            )}
          </div>

          <div className="row mb-2">
            <div className="col-12">
              <h5 className="mb-3">Department Summary</h5>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <div className="card radius-10 custom-shadow">
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={4} height={72} />
                  ) : departmentSummary.length > 0 ? (
                    <div className="row">
                      {departmentSummary.map((department) => (
                        <div className="col-12 col-md-6 col-xl-4 mb-3" key={department.id}>
                          <div className="border rounded p-3 h-100 bg-light">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="mb-0">{department.department}</h6>
                              <span className="badge bg-dark">{department.total}</span>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              <span className="badge text-bg-light border">
                                F-11: {department.f11}
                              </span>
                              <span className="badge text-bg-light border">
                                G-13: {department.g13}
                              </span>
                            </div>
                            <div className="small text-muted">
                              {department.topDesignations.length > 0
                                ? department.topDesignations
                                    .map(([name, count]) => `${name} (${count})`)
                                    .join(", ")
                                : "No designation breakdown"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3 mb-0">
                      No department summary found for the selected branch.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-12">
              <h5 className="mb-3">Attendance Summary</h5>
            </div>
          </div>

          <div className="row mb-4">
            {loading ? (
              <>
                <div className="col-12 col-lg-4 mb-3">
                  <Skeleton height={240} />
                </div>
                <div className="col-12 col-lg-4 mb-3">
                  <Skeleton height={240} />
                </div>
                <div className="col-12 col-lg-4 mb-3">
                  <Skeleton height={240} />
                </div>
                <div className="col-12 col-lg-6 mb-3">
                  <Skeleton height={140} />
                </div>
                <div className="col-12 col-lg-6 mb-3">
                  <Skeleton height={140} />
                </div>
              </>
            ) : (
              <>
                <AttendanceDayCard summary={attendanceSummary.yesterday} tone="secondary" />
                <AttendanceDayCard summary={attendanceSummary.today} tone="primary" />
                <AttendanceDayCard summary={attendanceSummary.tomorrow} tone="info" />
                <MetricSummaryCard
                  title="Late Summary"
                  tone="warning"
                  summaries={daySummaries}
                  keyName="late"
                />
                <MetricSummaryCard
                  title="Absent Summary"
                  tone="danger"
                  summaries={daySummaries}
                  keyName="absent"
                />
              </>
            )}
          </div>

          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">
                    <i className="bi bi-balloon-fill text-danger me-2"></i>
                    Birthdays This Month ({moment().format("MMMM")})
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : (
                    <DataTable
                      rows={birthdaysThisMonth}
                      emptyMessage="No birthdays this month."
                      columns={[
                        {
                          label: "Employee",
                          render: (row) => (
                            <strong>{`${row.first_name || ""} ${row.last_name || ""}`.trim()}</strong>
                          ),
                        },
                        { label: "Branch", key: "branch_name" },
                        {
                          label: "Designation",
                          render: (row) => row.designation_name || row.designation || "N/A",
                        },
                        {
                          label: "Department",
                          render: (row) => row.department_name || row.department || "N/A",
                        },
                        {
                          label: "Date",
                          render: (row) => (
                            <span className="badge bg-danger">{row.birthday_date}</span>
                          ),
                        },
                        {
                          label: "Age",
                          render: (row) => (
                            <span className="badge bg-info">{row.age} years</span>
                          ),
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">
                    <i className="bi bi-calendar-heart text-primary me-2"></i>
                    Anniversaries This Month ({moment().format("MMMM")})
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : (
                    <DataTable
                      rows={anniversariesThisMonth}
                      emptyMessage="No anniversaries this month."
                      columns={[
                        {
                          label: "Employee",
                          render: (row) => (
                            <strong>{`${row.first_name || ""} ${row.last_name || ""}`.trim()}</strong>
                          ),
                        },
                        { label: "Branch", key: "branch_name" },
                        {
                          label: "Designation",
                          render: (row) => row.designation_name || row.designation || "N/A",
                        },
                        {
                          label: "Department",
                          render: (row) => row.department_name || row.department || "N/A",
                        },
                        {
                          label: "Anniversary",
                          render: (row) => (
                            <span className="badge bg-primary">{row.anniversary_date}</span>
                          ),
                        },
                        {
                          label: "Years",
                          render: (row) => (
                            <span className="badge bg-success">
                              {row.years_of_service} years
                            </span>
                          ),
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card radius-10 custom-shadow mb-4">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">Details</h5>
                  <p className="text-muted mb-0 small">
                    All detailed views stay collapsed by default and appear only when opened.
                  </p>
                </div>
                <div className="card-body">
                  <div className="accordion" id="hr-dashboard-details">
                    <DetailAccordionItem
                      id="staff-register"
                      title="Staff Details"
                      tone="primary"
                      count={staffDetails.length}
                    >
                      <DataTable
                        rows={staffDetails}
                        emptyMessage="No staff records found."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Employee ID", key: "uid" },
                          { label: "Branch", key: "branch" },
                          { label: "Department", key: "department" },
                          { label: "Designation", key: "designation" },
                          {
                            label: "Joining Date",
                            render: (row) => formatDate(row.joining),
                          },
                          { label: "Phone", key: "phone" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="department-register"
                      title="Department Details"
                      tone="dark"
                      count={departmentSummary.length}
                    >
                      <DataTable
                        rows={departmentSummary}
                        emptyMessage="No department records found."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Department", key: "department" },
                          { label: "Total Staff", key: "total" },
                          { label: "F-11", key: "f11" },
                          { label: "G-13", key: "g13" },
                          { label: "Designation Breakdown", key: "designationSummary" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="present-register"
                      title="Present Details"
                      tone="success"
                      count={presentEmployees.length}
                    >
                      <DataTable
                        rows={presentEmployees}
                        emptyMessage="No present staff found in the current summary window."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          {
                            label: "Duty Hours",
                            render: (row) =>
                              row.duty_start_time !== "N/A"
                                ? `${row.duty_start_time} - ${row.duty_end_time}`
                                : "N/A",
                          },
                          { label: "Check In", key: "checkin_time" },
                          { label: "Check Out", key: "checkout_time" },
                          { label: "Working Hours", key: "working_hours" },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                          {
                            label: "Status",
                            render: (row) => (
                              <span
                                className={`badge bg-${
                                  row.status === "Late" ? "warning" : "success"
                                }`}
                              >
                                {row.status}
                              </span>
                            ),
                          },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="late-register"
                      title="Late Details"
                      tone="warning"
                      count={lateEmployees.length}
                    >
                      <DataTable
                        rows={lateEmployees}
                        emptyMessage="No late staff found in the current summary window."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          {
                            label: "Duty Hours",
                            render: (row) =>
                              row.duty_start_time !== "N/A"
                                ? `${row.duty_start_time} - ${row.duty_end_time}`
                                : "N/A",
                          },
                          { label: "Check In", key: "checkin_time" },
                          {
                            label: "Late By",
                            render: (row) => (
                              <span className="badge bg-warning">{row.late_time}</span>
                            ),
                          },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="absent-register"
                      title="Absent Details"
                      tone="danger"
                      count={absentEmployees.length}
                    >
                      <DataTable
                        rows={absentEmployees}
                        emptyMessage="No absent staff found in the current summary window."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          { label: "Reason", key: "reason" },
                          { label: "Remarks", key: "remarks" },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="leave-register"
                      title="Leave Details"
                      tone="info"
                      count={onLeaveEmployees.length}
                    >
                      <DataTable
                        rows={onLeaveEmployees}
                        emptyMessage="No approved leaves found in the current summary window."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          {
                            label: "Leave From",
                            render: (row) => formatDate(row.leave_from_date),
                          },
                          {
                            label: "Leave To",
                            render: (row) => formatDate(row.leave_to_date),
                          },
                          { label: "Leave Type", key: "leave_type" },
                          { label: "Reason", key: "reason" },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                        ]}
                      />
                    </DetailAccordionItem>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HRDashboard;
